import React, { useEffect } from 'react';

import getProductProps, { getAttachableProductProps } from '../product/get-product-props';
import { useSelector } from 'react-redux';
import useAddToCart from './useAddToCart';
import useDeviceInfo from './useDeviceInfo';
import useProductCompare from './useProductCompare';
import useProductPrice from './useProductPrice';
import { Helpers } from '../core';
import { EtrAPIClient } from '../core/src/etr-wcs-api-client';
const etrApiClient = new EtrAPIClient();

const EMPTY_OBJ = {};

/**
 * Check if a product is available based on price data
 * @param {Object} product - Product object with sku
 * @param {Object} prices - Price data object keyed by sku
 * @returns {boolean} True if product is available (has salePrice, not OOS, price fetch didn't fail)
 */
export const isProductAvailable = (product, prices) => {
    const { salePrice, isOOS, priceFetchFailed } = prices[product.sku] || { priceFetchFailed: true };
    return salePrice !== '' && !isOOS && !priceFetchFailed;
};

/**
 * Filter products to only those that are available
 * @param {Array} products - Array of product objects
 * @param {Object} prices - Price data object keyed by sku
 * @returns {Array} Filtered array of available products
 */
export const getAvailableProducts = (products, prices) => {
    // Normalizing to an empty array ensures .filter() always returns an array
    return (products || []).filter(p => isProductAvailable(p, prices));
};

/**
 * @typedef {Object} AttachableProductTileOptions
 * @property {Boolean} enablePageLoader - Set to true if you want to trigger the full page loader during add to cart
 * @property {Boolean} blockCartFlyout - Set to true if you block the normal cart preview flyout after add to cart
 * @property {Object} hooks - Optionally provide you own hooks for dependency injection
 */

/**
 * Returns helper functions for generating product props for AttachableProductTiles GFE component
 * @name useAttachableProductProps
 * @param {AttachableProductTileOptions} options
 * @returns {cartInfo}
 */
export const useAttachableProductProps = (options = EMPTY_OBJ) => {
    const { enablePageLoader, blockCartFlyout, hooks, trackAddToCart = true } = options;
    const addToCart = useAddToCart(trackAddToCart, { enablePageLoader });
    const { device } = useDeviceInfo(['device']);
    let cartHooks;
    //if cart flyout is unwanted, remove hook from dependency injection
    if (blockCartFlyout) {
        const { launchCartFlyout, ...rest } = addToCart;
        cartHooks = rest;
    } else {
        cartHooks = addToCart;
    }

    return [
        (product, price, onAttach, options = EMPTY_OBJ, analyticsData, trackCustomMetric, showOOS) => {
            const { linkTarget = '_blank', ...restOptions } = options;
            //HPEDT-11055 - on mobile link should alway open on the same page
            const linkTargetValue = device === 'mobile' ? '_self' : linkTarget;
            return getAttachableProductProps(
                product,
                price,
                onAttach,
                hooks || { addToCart: cartHooks },
                { ...restOptions, linkTarget: linkTargetValue },
                analyticsData,
                trackCustomMetric,
                showOOS,
            );
        },
    ];
};

export const useProductProps = (options = EMPTY_OBJ) => {
    const { enablePageLoader, blockCartFlyout, hooks, trackAddToCart = true } = options;
    const addToCart = useAddToCart(trackAddToCart, { enablePageLoader });
    const compare = useProductCompare();
    let cartHooks;
    //if cart flyout is unwanted, remove hook from dependency injection
    if (blockCartFlyout) {
        const { launchCartFlyout, ...rest } = addToCart;
        cartHooks = rest;
    } else {
        cartHooks = addToCart;
    }
    const productHooks = hooks || { addToCart: cartHooks, compare };
    return [
        (product, price, options = EMPTY_OBJ, analyticsData, trackCustomMetric, showOOS) => {
            return getProductProps(product, price, productHooks, options, analyticsData, trackCustomMetric);
        },
    ];
};

const NULL_PRODUCT = {};
/**
 * Returns product initial data for the product page
 * @name useProductInitial
 */
export const useProductInitial = () => {
    const productInitial = useSelector(state => state?.slugInfo?.components?.productInitial);
    return productInitial || NULL_PRODUCT;
};

export const useProductCards = (catEntryIds, options) => {
    const { filterOOS, includePrice = true } = options || {};
    const [productCards, setProductCards] = React.useState(null);
    // Convert catEntryIds to product objects for useProductPrice
    const productsForPricing = catEntryIds
        ? catEntryIds.map(id => (typeof id === 'object' ? id : { catEntryId: id, id }))
        : null;
    const { prices } = useProductPrice(includePrice ? productsForPricing : []);
    const fetchingRef = React.useRef(false);
    const lastIdsRef = React.useRef(null);

    useEffect(() => {
        if (!catEntryIds || catEntryIds.length === 0) {
            return;
        }

        const ids = catEntryIds.map(id => (typeof id === 'object' ? Helpers.getCatEntryId(id) : id)).filter(id => !!id);
        const idsKey = ids.join(',');

        // Skip if already fetching or if we've already fetched these exact IDs
        if (fetchingRef.current || lastIdsRef.current === idsKey) {
            return;
        }

        fetchingRef.current = true;
        lastIdsRef.current = idsKey;

        etrApiClient.product
            .getProductCards(ids)
            .then(data => {
                setProductCards(data);
                fetchingRef.current = false;
            })
            .catch(() => {
                setProductCards([]);
                fetchingRef.current = false;
                lastIdsRef.current = null;
            });
    }, [catEntryIds]);

    return { productCards: filterOOS ? getAvailableProducts(productCards, prices) : productCards, prices };
};
